import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { subscriptionApi } from '../services/subscriptionApi';

interface SubscriptionData {
  plan: {
    id: string;
    name: string;
    price: number;
    description: string;
  };
  customerInfo: {
    companyName: string;
    contactName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    businessType: string;
    employeeCount: string;
  };
}

const Contract: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [signature, setSignature] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const data = sessionStorage.getItem('subscriptionData');
    if (data) {
      setSubscriptionData(JSON.parse(data));
    } else {
      navigate('/subscription');
    }
  }, [navigate]);

  const handleSignContract = async () => {
    if (!signature.trim()) {
      setError('Please provide your digital signature');
      return;
    }

    if (!agreed) {
      setError('Please agree to the terms and conditions');
      return;
    }

    if (!subscriptionData) {
      setError('Subscription data not found');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const customerResponse = await subscriptionApi.createCustomer({
        companyName: subscriptionData.customerInfo.companyName,
        contactName: subscriptionData.customerInfo.contactName,
        email: subscriptionData.customerInfo.email,
        phone: subscriptionData.customerInfo.phone,
        address: subscriptionData.customerInfo.address,
        city: subscriptionData.customerInfo.city,
        state: subscriptionData.customerInfo.state,
        zipCode: subscriptionData.customerInfo.zipCode,
        businessType: subscriptionData.customerInfo.businessType,
        employeeCount: subscriptionData.customerInfo.employeeCount
      });

      const contractResponse = await subscriptionApi.createContract({
        customerId: customerResponse.customerId,
        planId: subscriptionData.plan.id,
        planName: subscriptionData.plan.name,
        monthlyFee: subscriptionData.plan.price,
        signature: signature,
        signatureDate: new Date().toISOString(),
        ipAddress: 'client-ip'
      });

      sessionStorage.setItem('contractData', JSON.stringify({
        contractId: contractResponse.contractId,
        customerId: customerResponse.customerId,
        amount: subscriptionData.plan.price
      }));

      navigate('/payment');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contract');
    } finally {
      setLoading(false);
    }
  };

  if (!subscriptionData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading contract information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-white shadow-sm">
        <div className="container py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/subscription')}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h1 className="text-xl font-bold">Service Contract</h1>
            <div className="w-16"></div>
          </div>
        </div>
      </div>

      <div className="container py-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-red-50 to-red-100 px-6 py-4 text-center">
              <FileText className="w-12 h-12 text-red-600 mx-auto mb-2" />
              <h2 className="text-2xl font-bold mb-1">IT Support Service Agreement</h2>
              <p className="text-gray-600">Please review and sign the service contract</p>
            </div>

            <div className="p-6">
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-bold mb-4">Service Agreement Terms</h3>
                    <div className="space-y-4 text-sm max-h-96 overflow-y-auto">
                      <section>
                        <h4 className="font-semibold mb-2">1. Service Details</h4>
                        <p className="text-gray-700">
                          This agreement is between Grid Managed Services and {subscriptionData.customerInfo.companyName} 
                          for {subscriptionData.plan.name} services at ${subscriptionData.plan.price} per month.
                        </p>
                      </section>
                      <section>
                        <h4 className="font-semibold mb-2">2. Scope of Services</h4>
                        <p className="text-gray-700">
                          Provider agrees to deliver comprehensive IT support services including system monitoring, 
                          technical support, security management, and maintenance services.
                        </p>
                      </section>
                      <section>
                        <h4 className="font-semibold mb-2">3. Service Level Agreement</h4>
                        <p className="text-gray-700">
                          Provider commits to maintaining system uptime of 99.9% and response times as specified 
                          in the selected service tier.
                        </p>
                      </section>
                      <section>
                        <h4 className="font-semibold mb-2">4. Payment Terms</h4>
                        <p className="text-gray-700">
                          Services are billed monthly in advance. Payment is due within 30 days of invoice date.
                        </p>
                      </section>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
                    <h3 className="text-lg font-bold mb-3 text-red-900">Service Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="font-medium text-red-800">Plan:</span>
                        <span className="text-red-700">{subscriptionData.plan.name}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="font-medium text-red-800">Monthly Fee:</span>
                        <span className="text-xl font-bold text-red-900">${subscriptionData.plan.price}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="font-medium text-red-800">Company:</span>
                        <span className="text-red-700">{subscriptionData.customerInfo.companyName}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-lg font-bold mb-3">Digital Signature</h3>
                    <div className="form-group">
                      <label className="form-label">Full Name (Digital Signature) *</label>
                      <input
                        type="text"
                        className={`form-input ${error && !signature.trim() ? 'border-red-500' : ''}`}
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                        placeholder="Type your full name"
                      />
                    </div>

                    <div className="mt-3">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          className="mt-1"
                        />
                        <span className="text-xs">
                          I have read, understood, and agree to the terms and conditions 
                          of this service agreement.
                        </span>
                      </label>
                    </div>

                    {error && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="text-red-700 text-xs">{error}</div>
                      </div>
                    )}
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-green-900 mb-1 text-sm">Secure & Compliant</h4>
                        <div className="text-green-800 text-xs space-y-1">
                          <div>• SOC 2 Type II Certified</div>
                          <div>• ISO 27001 Compliant</div>
                          <div>• 256-bit SSL Encryption</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSignContract}
                  disabled={loading}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      Sign Contract & Continue
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contract;